import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Star,
  AlertTriangle,
  RefreshCw,
  ClipboardList,
  Send,
  Lock,
  Eye,
  X,
  CheckCircle2,
  Undo2,
} from "lucide-react";
import {
  useAdminGetDietApplicationsQuery,
  useAdminPublishPreviewQuery,
  useAdminReleaseDietResultsMutation,
  useAdminUnpublishDietResultsMutation,
} from "../../api/examsApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

const STATUS_COLORS: Record<string, string> = {
  passed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  partial_pass: "bg-amber-50 text-amber-700 border-amber-200",
  referred: "bg-amber-50 text-amber-700 border-amber-200",
  recommended: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_recommended: "bg-rose-50 text-rose-700 border-rose-200",
  full_reset: "bg-rose-50 text-rose-700 border-rose-200",
  absent: "bg-gray-100 text-gray-500 border-gray-200",
  pending: "bg-gray-50 text-gray-500 border-gray-200",
};

/** Drafts and rejected applications never sat, so they are not in the sitting. */
const isCandidate = (app: any) => !["draft", "rejected"].includes(String(app?.status ?? ""));

function Stat({ label, value, tone }: { label: string; value: any; tone?: string }) {
  return (
    <div className="rounded-2xl border border-gray-100 px-4 py-3">
      <div className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-black tabular-nums ${tone ?? "text-gray-900"}`}>{value ?? 0}</div>
    </div>
  );
}

/**
 * Publication is final — a published result can no longer be graded, recompiled
 * or overridden — so the chief is shown exactly what the act will do before
 * committing to it, absentees named rather than counted.
 */
function PublishDialog({
  dietId,
  onClose,
  onPublished,
}: {
  dietId: string;
  onClose: () => void;
  onPublished: (msg: string) => void;
}) {
  const { data: preview, isLoading, isError } = useAdminPublishPreviewQuery({ dietId });
  const [publish, { isLoading: publishing }] = useAdminReleaseDietResultsMutation();
  const [error, setError] = useState<string | null>(null);

  const go = async () => {
    setError(null);
    try {
      const res: any = await publish({ dietId }).unwrap();
      onPublished(res?.message ?? "Results published.");
      onClose();
    } catch (e: any) {
      setError(e?.data?.message || "Could not publish. Please try again.");
    }
  };

  const nothingToDo = !!preview && preview.to_publish === 0;
  // Marked but never compiled: the API refuses the whole diet, so the button
  // must not offer an act that cannot happen.
  const blocked = (preview?.not_compiled ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-5 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">Publish results</h2>
            <p className="text-xs text-gray-400 font-medium mt-0.5">
              This closes the diet and releases every result to the candidates.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
            </div>
          ) : isError || !preview ? (
            <div className="text-sm text-rose-600 font-semibold">Could not work out what publishing would do.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Candidates" value={preview.candidates} />
                <Stat label="To publish" value={preview.to_publish} tone="text-indigo-600" />
                <Stat
                  label="Marked absent"
                  value={preview.to_mark_absent}
                  tone={preview.to_mark_absent > 0 ? "text-amber-600" : undefined}
                />
              </div>

              {preview.already_published > 0 && (
                <p className="text-xs font-semibold text-gray-500">
                  {preview.already_published} result(s) are already published and will be left exactly as they are.
                </p>
              )}

              {/* Interviews only — a panel's marks are the result, so there is
                  no separate compile step for them. */}
              {preview.to_compile > 0 && (
                <p className="text-xs font-semibold text-gray-500">
                  {preview.to_compile} interview result(s) will be compiled from the panel's marks as part of
                  publishing.
                </p>
              )}

              {blocked && (
                <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl px-4 py-3 flex gap-3">
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs text-rose-900 font-black uppercase tracking-widest">
                      Compile the diet first
                    </p>
                    <p className="text-xs text-rose-800 font-medium mt-1">
                      <span className="font-black">{preview.not_compiled}</span> candidate(s) have marks but no
                      compiled result. Publishing will not compile them — their verdict is yours to make.
                    </p>
                    <div className="mt-2 max-h-28 overflow-y-auto">
                      {preview.uncompiled.map((u) => (
                        <div
                          key={u.application_id}
                          className="text-[11px] font-bold text-rose-900 flex justify-between gap-3 py-0.5"
                        >
                          <span>{u.name ?? "—"}</span>
                          <span className="text-rose-500">{u.exam_no ?? "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {preview.incomplete_marking > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 font-medium">
                    <span className="font-black">{preview.incomplete_marking}</span> candidate(s) have courses that
                    are short of 3 examiners or missing marks. Publishing compiles them on what was actually
                    submitted.
                  </p>
                </div>
              )}

              {preview.to_mark_absent > 0 && (
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400 mb-2">
                    Will be recorded absent — no marks at all
                  </div>
                  <div className="border rounded-2xl divide-y divide-gray-50 max-h-48 overflow-y-auto">
                    {preview.absentees.map((a) => (
                      <div key={a.application_id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                        <span className="text-sm font-bold text-gray-800">{a.name ?? "—"}</span>
                        <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                          {a.exam_no ?? "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3 flex gap-3">
                <Lock className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <p className="text-xs text-rose-800 font-medium">
                  Once published, these results are final. You will no longer be able to grade, recompile or
                  override any of them.
                </p>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-4 py-3 flex gap-3">
                <Eye className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                <p className="text-xs text-indigo-800 font-medium">
                  Candidates will <span className="font-black">not</span> see anything yet. The secretariat
                  releases the diet once you have published it.
                </p>
              </div>
            </>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3 text-xs font-bold text-rose-700">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border text-[10px] font-black uppercase tracking-[0.15em] text-gray-500 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={go}
            disabled={publishing || isLoading || nothingToDo || blocked}
            className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-xl px-6 py-2.5 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-primary disabled:opacity-40 transition-all active:scale-95"
          >
            {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {blocked
              ? "Compile first"
              : nothingToDo
                ? "Nothing to publish"
                : `Publish ${preview?.to_publish ?? ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChiefDietGradingPage() {
  const { dietId = "" } = useParams();
  const navigate = useNavigate();

  const {
    data: apps = [],
    isLoading,
    isFetching,
    refetch,
  } = useAdminGetDietApplicationsQuery({ dietId }, { skip: !dietId });

  const rows = apps as any[];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [unpublish, { isLoading: unpublishing }] = useAdminUnpublishDietResultsMutation();

  // Publishing closes grading and locks the marks; unpublishing takes that back.
  // Both are the same capability, and without it the API refuses — so the
  // buttons come off the page rather than failing when pressed.
  const { can } = useAccess();
  const canPublish = can.perm(PERMS.exam.resultPublish);

  const doUnpublish = async () => {
    if (
      !window.confirm(
        `Reopen ${reopenable} published result(s) for grading?\n\n` +
          "Publishing is undone for those: marks, compilation and emergency override " +
          "become available again, and candidates nobody marked go back to ungraded.\n\n" +
          "Anything already released to candidates is left untouched.",
      )
    )
      return;
    try {
      const res: any = await unpublish({ dietId }).unwrap();
      setBanner(res?.message ?? "Diet reopened for grading.");
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not reopen this diet.");
    }
  };

  const stats = useMemo(() => {
    const candidates = rows.filter(isCandidate);
    return {
      candidates: candidates.length,
      // finalized_at is the board's publication; published_at is the
      // secretariat's release to the candidate, which happens afterwards.
      published: candidates.filter((a) => a.result?.finalized_at || a.result?.published_at).length,
      released: candidates.filter((a) => a.result?.published_at).length,
      graded: candidates.filter((a) => a.result && a.result.overall_status !== "pending").length,
    };
  }, [rows]);

  // Grading is closed once every candidate in the diet carries a published result.
  const allPublished = stats.candidates > 0 && stats.published === stats.candidates;
  const allReleased = stats.candidates > 0 && stats.released === stats.candidates;
  // Published but not yet released — the only results the board can still take
  // back. A released one belongs to the candidate.
  const reopenable = Math.max(0, stats.published - stats.released);

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4 md:p-6 pb-20">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate(-1)}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <ClipboardList className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">
                Grade / Override Results
              </h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">
                Open a candidate to grade, compile, or apply an emergency override.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 text-indigo-500 ${isFetching ? "animate-spin" : ""}`} />
            </button>

            {/* Both actions are always on the page for any diet with
                candidates — hiding one left the chief guessing whether the diet
                was in the wrong state or the button had simply not loaded. Each
                disables itself and says why. */}
            {allPublished && (
              <span className="inline-flex items-center gap-2 rounded-xl px-4 py-3 text-[10px] font-black uppercase tracking-[0.15em] border bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle2 className="w-3.5 h-3.5" /> Published
              </span>
            )}

            {stats.candidates > 0 && canPublish && (
              <button
                onClick={() => setDialogOpen(true)}
                disabled={isLoading || allPublished}
                title={
                  allPublished
                    ? "Every candidate in this diet is already published"
                    : `Publish the ${stats.candidates - stats.published} outstanding result(s) in this diet`
                }
                className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-primary disabled:opacity-40 transition-all active:scale-95 shadow-sm"
              >
                <Send className="w-3.5 h-3.5" /> Publish all results
              </button>
            )}

            {/* Released results can never be reopened, so this is live only
                while something published is still unreleased. */}
            {stats.published > 0 && canPublish && (
              <button
                onClick={doUnpublish}
                disabled={unpublishing || reopenable === 0}
                title={
                  reopenable === 0
                    ? "Every published result here has been released to candidates and cannot be reopened"
                    : reopenable < stats.published
                      ? `Reopen the ${reopenable} published result(s) that have not been released`
                      : "Undo publishing and reopen this diet for grading"
                }
                className="inline-flex items-center gap-2 bg-rose-600 text-white rounded-xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-rose-700 disabled:opacity-40 transition-all active:scale-95 shadow-sm"
              >
                {unpublishing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Undo2 className="w-3.5 h-3.5" />
                )}
                {unpublishing ? "Unpublishing…" : "Unpublish all results"}
              </button>
            )}
          </div>
        </div>

        {stats.candidates > 0 && (
          <div className="mt-6 grid grid-cols-3 gap-3">
            <Stat label="Candidates" value={stats.candidates} />
            <Stat
              label="Published"
              value={stats.published}
              tone={allPublished ? "text-emerald-600" : "text-gray-900"}
            />
            <Stat
              label="Released to candidates"
              value={stats.released}
              tone={allReleased ? "text-emerald-600" : "text-gray-400"}
            />
          </div>
        )}
      </div>

      {banner && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 flex items-center gap-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <p className="text-xs text-emerald-800 font-bold">{banner}</p>
        </div>
      )}

      {/* Publication state / emergency-override hint */}
      {allPublished ? (
        <div className="bg-gray-900 rounded-2xl px-5 py-4 flex items-start gap-3">
          <Lock className="w-4 h-4 text-white shrink-0 mt-0.5" />
          <p className="text-xs text-white font-medium">
            These results are <span className="font-black">published and final</span>. Grades, compilation and
            emergency overrides are closed for this diet.{" "}
            {allReleased ? (
              <>The secretariat has released them — candidates can see their results.</>
            ) : (
              <span className="text-white/70">
                Candidates cannot see them yet — the secretariat releases the diet. Until then you can
                still unpublish to reopen grading.
              </span>
            )}
          </p>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800 font-medium">
            Opening a candidate lets you grade normally (≥3 examiner average) or use the{" "}
            <span className="font-black">Emergency Override</span> to set the final result directly. Finish
            marking and compiling first — publishing then records anyone with no marks{" "}
            <span className="font-black">absent</span> and closes the diet. After that nothing can be changed,
            and the secretariat releases the results to candidates.
          </p>
        </div>
      )}

      {/* Candidates */}
      <div className="bg-white border border-gray-100 rounded-[2rem] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
          Candidates {rows.length > 0 ? `(${rows.length})` : ""}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
            No candidates in this diet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Candidate</th>
                  <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Exam No</th>
                  <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Result</th>
                  <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((app: any) => {
                  const result = app.result;
                  // A published result is closed to editing — the screen offers
                  // to open it, not to change it. published_at (the secretariat's
                  // release) implies it too.
                  const published = !!(result?.finalized_at || result?.published_at);
                  const released = !!result?.published_at;
                  return (
                    <tr key={app.id} className="hover:bg-gray-50/80 transition-all">
                      <td className="px-6 py-4">
                        <div className="font-black text-gray-900">{app.user?.name ?? "—"}</div>
                        <div className="text-[10px] font-bold text-gray-400">{app.user?.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        {app.exam_no ? (
                          <span className="font-black text-[11px] text-gray-900 uppercase tracking-wider">{app.exam_no}</span>
                        ) : (
                          <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border bg-gray-50 text-gray-500 border-gray-200">
                          {String(app.status ?? "").replace(/_/g, " ") || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {result ? (
                          <div className="flex flex-col gap-1">
                            <span className={`inline-block w-fit px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${STATUS_COLORS[result.overall_status] ?? "bg-gray-50 text-gray-500 border-gray-200"}`}>
                              {String(result.overall_status ?? "").replace(/_/g, " ")}
                            </span>
                            {published && (
                              <span
                                className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border bg-gray-900 text-white border-gray-900"
                                title={`Published ${result.finalized_at ?? result.published_at}`}
                              >
                                <Lock className="w-2.5 h-2.5 shrink-0" /> Published
                              </span>
                            )}
                            {released && (
                              <span
                                className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border bg-emerald-50 text-emerald-700 border-emerald-200"
                                title={`Released to the candidate ${result.published_at}`}
                              >
                                <Eye className="w-2.5 h-2.5 shrink-0" /> Released
                              </span>
                            )}
                            {result.is_override && (
                              <span
                                className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border bg-amber-50 text-amber-700 border-amber-200"
                                title={
                                  `Emergency override by the Board Chairman` +
                                  (result.override_by_name ? ` (${result.override_by_name})` : "") +
                                  (result.override_reason ? ` — ${result.override_reason}` : "")
                                }
                              >
                                <AlertTriangle className="w-2.5 h-2.5 shrink-0" /> Overridden
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Not graded</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link
                          to={`/exams/admin/applications/${app.id}/course-grading`}
                          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[9px] font-black uppercase tracking-[0.15em] transition-all active:scale-95 ${
                            published
                              ? "border bg-white text-gray-500 hover:bg-gray-50"
                              : "bg-gray-900 text-white hover:bg-primary"
                          }`}
                        >
                          {published ? (
                            <>
                              <Eye className="w-3 h-3" /> View
                            </>
                          ) : (
                            <>
                              <Star className="w-3 h-3" /> Grade / Override
                            </>
                          )}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dialogOpen && (
        <PublishDialog dietId={dietId} onClose={() => setDialogOpen(false)} onPublished={setBanner} />
      )}
    </div>
  );
}
